import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
    Globe, Plus, Trash2, Link, Copy, Check,
    CheckCircle, XCircle, Clock, Network, RotateCw, ArrowRight, X
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { toast } from '@/lib/toast';
import { Button, Card, EmptyState, IconButton, Input, ListPageSkeleton, Modal, PageHeader, Select, StatCard, StatusBadge } from '@/components/ds-zta';
import { getRoles } from '../services/iam';
import { useDialogs } from '@/components/dialogs/DialogProvider';
import { errorMessage } from '@/lib/http';
import { clustersApi, cleanRoleMappings, type RoleMapping } from '@/features/trusted-clusters/trustedClusters';
import { copyWithToast } from '@/lib/copy';
import { BRAND } from '@/brand';

/** Relative time for a timestamp, or null when it is unparseable
 *  (date-fns throws "Invalid time value" on an Invalid Date). */
const safeDistanceToNow = (value: string | number | Date) => {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : formatDistanceToNow(d, { addSuffix: true });
};

export default function TrustedClusters() {
    const [showAddModal, setShowAddModal] = useState(false);
    const [joinToken, setJoinToken] = useState('');
    const [copied, setCopied] = useState(false);
    const [newCluster, setNewCluster] = useState({ name: '', remote_api_url: '', role_mappings: [] as RoleMapping[] });
    // Local roles for the mapping editor (design eval F28). POST
    // /trusted-clusters takes role_mappings; there is no update endpoint, so
    // a cluster's mappings are set when it is added and read-only after.
    const { data: rolesData } = useQuery(['roles', 'all'], () => getRoles(), { enabled: showAddModal, staleTime: 60_000 });
    const localRoles = rolesData?.data ?? [];
    const { mappings: cleanMappings, incomplete: incompleteMappings } = cleanRoleMappings(newCluster.role_mappings);
    const setMapping = (i: number, patch: Partial<RoleMapping>) =>
        setNewCluster((c) => ({ ...c, role_mappings: c.role_mappings.map((m, j) => (j === i ? { ...m, ...patch } : m)) }));
    const queryClient = useQueryClient();
    const { confirm } = useDialogs();

    const { data, isLoading, isFetching, isError, refetch } = useQuery('trusted-clusters', clustersApi.list);

    const createMutation = useMutation(clustersApi.create, {
        onSuccess: (data) => {
            queryClient.invalidateQueries('trusted-clusters');
            // Close the form only once the server has accepted it; on error it stays
            // open with the operator's input intact so they can correct and retry.
            setShowAddModal(false);
            setNewCluster({ name: '', remote_api_url: '', role_mappings: [] });
            if (data?.join_token) {
                setJoinToken(data.join_token);
            }
            toast.success('Trusted cluster created');
        },
        onError: (err) => { toast.error(errorMessage(err, 'Failed to create cluster')); },
    });

    const deleteMutation = useMutation(clustersApi.remove, {
        onSuccess: () => {
            queryClient.invalidateQueries('trusted-clusters');
            toast.success('Cluster removed');
        },
        onError: (err) => { toast.error(errorMessage(err, 'Failed to remove cluster')); },
    });

    const handleCreate = () => {
        createMutation.mutate({ ...newCluster, role_mappings: cleanMappings });
    };

    const handleCopyToken = async () => {
        if (!(await copyWithToast(joinToken, { success: false }))) return;
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'active': return <CheckCircle className="h-5 w-5 text-[var(--d2d-success)]" />;
            case 'disabled': return <XCircle className="h-5 w-5 text-[var(--danger-text)]" />;
            default: return <Clock className="h-5 w-5 text-[var(--d2d-warning)]" />;
        }
    };

    const clusters = data?.clusters || [];

    return (
        <div>
            <PageHeader
                className="mb-8"
                title="Trusted Clusters"
                icon={<Network />}
                description={`Federate trust across multiple ${BRAND.name} clusters`}
                onRefresh={() => refetch()}
                refreshing={isFetching}
                primaryAction={
                    <Button onClick={() => setShowAddModal(true)} leadingIcon={<Plus className="h-4 w-4" />}>
                        Add Cluster
                    </Button>
                }
            />

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <StatCard icon={<Globe />} label="Total Clusters" value={clusters.length} />
                <StatCard icon={<CheckCircle />} accent="success" label="Active" value={clusters.filter((c: any) => c.status === 'active').length} />
                <StatCard icon={<Clock />} accent="warning" label="Pending" value={clusters.filter((c: any) => c.status === 'pending').length} />
            </div>

            {/* Clusters List */}
            <Card className="overflow-hidden">
                {isLoading ? (
                    <ListPageSkeleton className="p-4" filterCount={0} rowCount={6} />
                ) : isError ? (
                    <EmptyState
                        icon={<Network />}
                        title="Failed to load trusted clusters"
                        description="Something went wrong while loading trusted clusters."
                        action={
                            <Button variant="secondary" onClick={() => refetch()} leadingIcon={<RotateCw className="h-4 w-4" />}>
                                Retry
                            </Button>
                        }
                    />
                ) : clusters.length === 0 ? (
                    <EmptyState
                        icon={<Network />}
                        title="No trusted clusters yet"
                        description="Add a cluster to enable cross-cluster resource access."
                        action={
                            <Button onClick={() => setShowAddModal(true)} leadingIcon={<Plus className="h-4 w-4" />}>
                                Add Cluster
                            </Button>
                        }
                    />
                ) : (
                    <div className="divide-y divide-[var(--d2d-border)]">
                        {clusters.map((cluster: any) => (
                            <div key={cluster.id} className="p-6 hover:bg-[var(--ds-color-bg-hover)] transition-colors">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-4">
                                        {getStatusIcon(cluster.status)}
                                        <div>
                                            <h3 className="text-lg font-medium text-[var(--d2d-text-primary)]">{cluster.name}</h3>
                                            <p className="text-sm text-[var(--d2d-text-muted)] flex items-center gap-1">
                                                <Link className="h-3 w-3" /> {cluster.remote_api_url}
                                            </p>
                                            {cluster.last_heartbeat && (
                                                <p className="text-xs text-[var(--d2d-text-muted)] mt-1">
                                                    Last heartbeat: {safeDistanceToNow(cluster.last_heartbeat) ?? '—'}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        {/* An active trust link is a connected cluster in the shared vocabulary. */}
                                        <StatusBadge shape="chip" value={cluster.status === 'active' ? 'connected' : cluster.status}>
                                            {cluster.status}
                                        </StatusBadge>
                                        <IconButton
                                            aria-label="Remove cluster"
                                            tooltip="Remove cluster"
                                            size="sm"
                                            onClick={async () => {
                                                if (await confirm({ title: 'Remove this cluster?', message: `Remove trusted cluster "${cluster.name}"?`, confirmLabel: 'Remove', variant: 'danger' })) deleteMutation.mutate(cluster.id);
                                            }}
                                            className="text-[var(--danger-text)]"
                                            icon={<Trash2 className="h-4 w-4" />}
                                        />
                                    </div>
                                </div>
                                <div className="mt-3 flex flex-wrap items-center gap-2" aria-label={`Role mappings for ${cluster.name}`}>
                                    <span className="text-xs text-[var(--d2d-text-muted)]">Role mappings:</span>
                                    {cluster.role_mappings?.length > 0 ? (
                                        cluster.role_mappings.map((rm: RoleMapping, i: number) => (
                                            <span key={i} className="text-xs bg-[var(--ds-color-bg-subtle)] px-2 py-1 rounded">
                                                {rm.remote_role} → {rm.local_role}
                                            </span>
                                        ))
                                    ) : (
                                        <span className="text-xs text-[var(--d2d-text-muted)]">None</span>
                                    )}
                                    <span className="text-xs text-[var(--d2d-text-muted)]">
                                        · Read-only: mappings can't be edited after a cluster is added yet.
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Card>

            {/* Join Token Modal */}
            <Modal
                dismissOnOverlay={false}
                open={!!joinToken}
                onClose={() => setJoinToken('')}
                title="Join Token Generated"
                description="Use this token on the remote cluster to complete federation. It expires in 24 hours."
                footer={<Button className="w-full" onClick={() => setJoinToken('')}>Done</Button>}
            >
                <div className="flex items-center gap-2 bg-[var(--ds-color-bg-subtle)] p-3 rounded font-mono text-sm break-all">
                    <span className="flex-1">{joinToken}</span>
                    <IconButton
                        aria-label="Copy token"
                        tooltip="Copy token"
                        size="sm"
                        onClick={handleCopyToken}
                        className="flex-shrink-0"
                        icon={copied ? <Check className="h-4 w-4 text-[var(--d2d-success)]" /> : <Copy className="h-4 w-4" />}
                    />
                </div>
            </Modal>

            {/* Add Modal */}
            <Modal
                dismissOnOverlay={false}
                open={showAddModal}
                onClose={() => { if (!createMutation.isLoading) setShowAddModal(false); }}
                title="Add Trusted Cluster"
                footer={
                    <>
                        <Button variant="secondary" className="flex-1" onClick={() => setShowAddModal(false)} disabled={createMutation.isLoading}>Cancel</Button>
                        <Button className="flex-1" onClick={handleCreate} loading={createMutation.isLoading} disabled={!newCluster.name || !newCluster.remote_api_url || incompleteMappings > 0 || createMutation.isLoading}>
                            Create & Generate Token
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <Input
                        label="Cluster Name"
                        type="text"
                        value={newCluster.name}
                        onChange={e => setNewCluster({ ...newCluster, name: e.target.value })}
                        placeholder="production-us-east"
                    />
                    <Input
                        label="Remote API URL"
                        type="text"
                        value={newCluster.remote_api_url}
                        onChange={e => setNewCluster({ ...newCluster, remote_api_url: e.target.value })}
                        placeholder="https://iap-admin.remote-cluster.example.com"
                    />
                    <fieldset className="space-y-2">
                        <legend className="text-sm font-medium text-[var(--d2d-text-primary)]">Role mappings</legend>
                        <p className="text-xs text-[var(--d2d-text-muted)]">
                            Map a role on the remote cluster to a role here. A remote user gets only the mapped local roles.
                            Mappings are set now: they can't be edited after the cluster is added yet.
                        </p>
                        {newCluster.role_mappings.map((m, i) => (
                            <div key={i} className="flex items-end gap-2">
                                <Input
                                    className="flex-1"
                                    label={`Remote role ${i + 1}`}
                                    value={m.remote_role}
                                    onChange={(e) => setMapping(i, { remote_role: e.target.value })}
                                    placeholder="remote-admin"
                                />
                                <ArrowRight className="mb-3 h-4 w-4 flex-none text-[var(--d2d-text-muted)]" aria-hidden="true" />
                                <Select
                                    className="flex-1"
                                    label={`Local role ${i + 1}`}
                                    value={m.local_role}
                                    onChange={(e) => setMapping(i, { local_role: e.target.value })}
                                >
                                    <option value="">Choose a role</option>
                                    {m.local_role && !localRoles.some((r) => (r.key || r.name) === m.local_role) && (
                                        <option value={m.local_role}>{m.local_role}</option>
                                    )}
                                    {localRoles.map((r) => (
                                        <option key={r.id} value={r.key || r.name}>{r.name}</option>
                                    ))}
                                </Select>
                                <IconButton
                                    aria-label={`Remove role mapping ${i + 1}`}
                                    tooltip="Remove mapping"
                                    size="sm"
                                    className="mb-1.5"
                                    onClick={() => setNewCluster((c) => ({ ...c, role_mappings: c.role_mappings.filter((_, j) => j !== i) }))}
                                    icon={<X className="h-4 w-4" />}
                                />
                            </div>
                        ))}
                        {incompleteMappings > 0 && (
                            <p className="text-xs text-[var(--danger-text)]" role="alert">
                                Fill in both roles of each mapping, or remove it.
                            </p>
                        )}
                        <Button
                            variant="secondary"
                            size="sm"
                            leadingIcon={<Plus className="h-4 w-4" />}
                            onClick={() => setNewCluster((c) => ({ ...c, role_mappings: [...c.role_mappings, { remote_role: '', local_role: '' }] }))}
                        >
                            Add mapping
                        </Button>
                    </fieldset>
                </div>
            </Modal>
        </div>
    );
}
